<template>
  <div class="rule-form-step flex max-w-4xl flex-col gap-5 py-5">
    <form-block
      :title="tr('Overview')"
      :description="description"
    />

    <div v-if="(!isListEmpty || isLoading) && !isAiMode" class="flex gap-3 max-sm:flex-col">
      <div class="relative flex-1">
        <search-icon class="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
        <ui-input
          v-model="searchTerm"
          class="pl-9"
          :placeholder="tr('Search') + '...'"
          :disabled="isLoading"
        />
      </div>
      <ui-select v-model="codeFilter" :disabled="isLoading">
        <ui-select-trigger class="w-full sm:w-56" :aria-label="tr('Status')">
          <ui-select-value />
        </ui-select-trigger>
        <ui-select-content>
          <ui-select-item v-for="option in codeFilterOptions" :key="option.codes" :value="option.codes">
            {{ tr(option.label) }}
          </ui-select-item>
        </ui-select-content>
      </ui-select>
    </div>

    <template v-if="!isLoading && filteredList.length === 0">
      <empty-screen
        v-if="isAiMode"
        class="my-5"
        icon="hourglass"
        :title="tr('Infinite∞Ai is still analyzing your contents')"
        :flag-text="tr('Wait a moment')"
        :description="aiEmptyScreenDescription"
      >
        <template #under-description>
          <ui-button @click="() => loadActivityOverview(true)">
            <refresh-cw-icon />
            {{ tr('Refresh') }}
          </ui-button>
        </template>
      </empty-screen>
      <empty-screen
        v-else
        class="my-5"
        icon="search"
        :title="tr('No results match your current search criteria')"
        :flag-text="tr('Modify search')"
        :description="tr('Currently there are no items that match your criteria. The items you\'re looking for might appear here later, or try different parameters to see currently available items.')"
      >
        <template #under-description>
          <ui-button @click="() => loadActivityOverview(true)">
            <refresh-cw-icon />
            {{ tr('Refresh') }}
          </ui-button>
        </template>
      </empty-screen>
    </template>

    <div v-else class="flex flex-col gap-2.5">
      <template v-if="isLoading">
        <div v-for="i in 3" :key="'rule-activitity-overview-skeleton-' + i" class="glass-tile flex flex-col overflow-hidden rounded-xl">
          <span class="px-3 py-2"><ui-skeleton class="h-3 w-40" /></span>
          <span class="grid items-center gap-4 p-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
            <social-page />
            <inline-social-content />
          </span>
        </div>
      </template>

      <component
        :is="isOpenable(item.code) ? 'button' : 'div'"
        v-for="item in renderedList"
        v-else
        :key="item.entity.id"
        :type="isOpenable(item.code) ? 'button' : null"
        data-slot="review-item"
        :class="[
          'glass-tile flex w-full flex-col overflow-hidden rounded-xl text-left',
          isOpenable(item.code) ? 'cursor-pointer ' + GLOW_TRANSITION + ' ' + headerStyle(item.code).glow : '',
        ]"
        @click="() => openFullscreenModal(item)"
      >
        <span :class="[ 'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white', headerStyle(item.code).bg ]">
          <component :is="headerStyle(item.code).icon" v-if="headerStyle(item.code).icon" class="size-3.5 shrink-0" />
          {{ getHeaderLabel(item.code) }}
        </span>
        <span class="grid items-center gap-4 p-3 sm:grid-cols-[11rem_minmax(0,1fr)_1.25rem]">
          <social-page
            :source-profile="item.post.source_profile"
            :date="item.entity.created_at"
            :date-tooltip-prefix="getDateTooltipPrefix(item)"
          />
          <inline-social-content
            :entity="item.entity"
            :badge="item.affected_ad_count > 1 ? tr('Total :count ad variants', { ':count': item.affected_ad_count }) : null"
          />
          <chevron-right-icon v-if="isOpenable(item.code)" class="size-5 text-destructive max-sm:hidden" />
        </span>
      </component>
    </div>

    <div ref="scrollPagerTrigger" />
  </div>
</template>

<script>
import { ChevronRight, CircleCheck, CircleX, Clock, RefreshCw, Search } from '@lucide/vue';
import { markRaw } from 'vue';

import { EVENT_BUS_EVENTS, RULE_ACTION, RULE_REVIEW_CODE, RULE_REVIEW_CODE_TYPE, RULE_REVIEW_FILTER_DEFINITIONS } from '@/consts.js';
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import RuleService from '@/services/Rule.js';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import FormBlock from '@/ui/components/FormBlock.vue';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import RuleActivityReview from '@/ui/components/RuleActivityReview.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import { GLOW_TRANSITION } from '@/ui/utils/ruleAction.js';
import debounce from '@/utils/debounce.js';
import EventBus from '@/utils/eventBus.js';

const ITEMS_PER_PAGE = 10;

export default {
  name: 'RuleFormReviewView',
  components: {
    ChevronRightIcon: ChevronRight,
    EmptyScreen,
    FormBlock,
    InlineSocialContent,
    RefreshCwIcon: RefreshCw,
    SearchIcon: Search,
    SocialPage,
  },
  mixins: [ ScrollPagerMixin ],
  data() {
    return {
      GLOW_TRANSITION,
      debouncedRecalculateFilteredList: debounce(() => this.recalculateFilteredList(), 500),
      isLoading: false,
      searchTerm: '',
      codeFilter: '[]',
      list: [],
      filteredList: [],
      renderedList: [],
    };
  },
  computed: {
    isAiMode() {
      return this.$store.getters['ruleForm/isAiMode'];
    },
    aiEmptyScreenDescription() {
      return this.tr('Infinite∞Ai is still searching for your best-performing content. Please wait a few moments and click the "Refresh" button, or you can start preparing your AI-supported automations using the "Create" button above.');
    },
    entity() {
      return this.$store.getters['ruleForm/entity'];
    },
    description() {
      if (this.isAiMode) {
        return this.tr('Based on the provided settings and Infinite∞Ai analysis, these contents will initially be used to create ads. Subsequently, the Ai will continuously review your content and automatically create new ads from the best-performing ones.');
      }

      switch (this.entity.action) {
        case RULE_ACTION.START:
          return this.tr('Here is the list of content currently being used to create ads based on the set criteria. Items marked with a red notification bar cannot be used as ads or are experiencing issues that prevent them from running. Click on these items for more details.');

        case RULE_ACTION.STOP:
          return this.tr('Here is the list of ads that match your criteria for being stopped. Items marked with a red notification bar cannot be stopped or are experiencing issues that prevent them from being processed. Click on these items for more details.');

        case RULE_ACTION.SCALING_UP:
          return this.tr('Here is the list of ads that match your criteria for being scaled up. Items marked with a red notification bar cannot be scaled up or are experiencing issues that prevent them from being processed. Click on these items for more details.');
      }

      return null;
    },
    codeFilterOptions() {
      const typeGroupLabels = [
        {
          label: 'Waiting for Processing',
          type: RULE_REVIEW_CODE_TYPE.WAITING,
        },
        {
          label: 'Completed',
          type: RULE_REVIEW_CODE_TYPE.COMPLETED,
        },
        {
          label: 'Excluded by Filters',
          type: RULE_REVIEW_CODE_TYPE.FILTERED_OUT,
        },
        {
          label: 'Content Issue',
          type: RULE_REVIEW_CODE_TYPE.CONTENT_ISSUE,
        },
        {
          label: 'Technical Restriction',
          type: RULE_REVIEW_CODE_TYPE.TECHNICAL_RESTRICTION,
        },
      ];

      const result = [];

      typeGroupLabels.forEach(group => {
        const codes = RULE_REVIEW_FILTER_DEFINITIONS.filter(item => item.type === group.type);

        result.push({
          label: group.label,
          codes: JSON.stringify(codes.map(item => item.code)),
        });
      });

      result.unshift({
        label: 'All',
        codes: JSON.stringify([]),
      });

      return result;
    },
    contentLabel() {
      if (this.entity.action === RULE_ACTION.START) {
        return this.tr('Post Content');
      }

      return this.tr('Ad Content');
    },
    isListEmpty() {
      return this.list.length === 0;
    },
    hasMore() {
      return this.renderedList.length !== this.filteredList.length;
    },
    language() {
      return this.$store.getters['app/language'];
    },
  },
  watch: {
    list() {
      this.recalculateFilteredList();
    },
    searchTerm() {
      this.debouncedRecalculateFilteredList();
    },
    codeFilter() {
      this.recalculateFilteredList();
    },
    language() {
      this.list = [];
      this.loadActivityOverview(true);
    },
  },
  created() {
    this.loadActivityOverview();
  },
  mounted() {
    EventBus.$on(EVENT_BUS_EVENTS.PROHIBITED_CONTENT_OVERRIDDEN, this.onProhibitedContentOverridden);
    EventBus.$on(EVENT_BUS_EVENTS.WAITING_ACTIVITY_ACCEPTED, this.onWaitingActivityAccepted);
    EventBus.$on(EVENT_BUS_EVENTS.WAITING_ACTIVITY_REJECTED, this.onWaitingActivityRejected);
  },
  beforeUnmount() {
    EventBus.$off(EVENT_BUS_EVENTS.PROHIBITED_CONTENT_OVERRIDDEN, this.onProhibitedContentOverridden);
    EventBus.$off(EVENT_BUS_EVENTS.WAITING_ACTIVITY_ACCEPTED, this.onWaitingActivityAccepted);
    EventBus.$off(EVENT_BUS_EVENTS.WAITING_ACTIVITY_REJECTED, this.onWaitingActivityRejected);
  },
  methods: {
    onProhibitedContentOverridden(postAiProhibitedContentEntity) {
      this.changeItemCode(postAiProhibitedContentEntity, RULE_REVIEW_CODE.WAITING_FOR_PROCESS);
    },
    onWaitingActivityAccepted(ruleActivityManagementQueueItemEntity) {
      this.changeItemCode(ruleActivityManagementQueueItemEntity, RULE_REVIEW_CODE.WAITING_FOR_PROCESS);
    },
    onWaitingActivityRejected(ruleActivityManagementQueueItemEntity) {
      this.changeItemCode(ruleActivityManagementQueueItemEntity, RULE_REVIEW_CODE.REJECTED_MANUALLY_IN_ACTIVITY_MANAGEMENT);
    },
    changeItemCode(relatedEntity, newCode) {
      const item = this.list.find(item => item.related_entity && item.related_entity.id === relatedEntity.id);
      if (!item) {
        return;
      }

      item.code = newCode;
      item.related_entity.updateWith(relatedEntity);
    },
    recalculateFilteredList() {
      let result = [ ...this.list ];

      if (this.searchTerm.trim().length > 0) {
        const searchTerm = this.searchTerm.toLowerCase();
        result = result.filter(item => {
          const content = this.getContent(item).toLowerCase();
          return content.includes(searchTerm);
        });
      }

      if (this.codeFilter !== '[]') {
        const selectedCodes = JSON.parse(this.codeFilter);
        result = result.filter(item => selectedCodes.includes(item.code));
      }

      this.filteredList = result;
      this.renderedList = [];

      this.showMore();
    },
    showMore() {
      if (!this.hasMore) {
        return;
      }

      const currentPageItems = this.filteredList.slice(this.renderedList.length, this.renderedList.length + ITEMS_PER_PAGE);
      this.renderedList.push(...currentPageItems);

      this.$nextTick(() => {
        if (this.hasMore) {
          this.initScrollPager('scrollPagerTrigger', 'showMore');
        }
      });
    },
    loadActivityOverview(updatePosts) {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      let promise;
      if (this.isAiMode) {
        promise = RuleService.generateActivityReviewForAiMode(
          this.entity,
          this.$store.getters['ruleForm/goal'],
          this.$store.getters['ruleForm/scalingBudget'],
          this.$store.getters['ruleForm/aiAssist'],
          updatePosts,
        );
      }
      else {
        promise = RuleService.generateActivityReview(this.entity, updatePosts);
      }

      promise
        .then(resp => this.list = resp)
        // eslint-disable-next-line no-console
        .catch(err => console.error(err))
        .finally(() => this.isLoading = false);
    },
    getRuleActivityItemDefinition(code) {
      return RULE_REVIEW_FILTER_DEFINITIONS.find(item => item.code === code);
    },
    isOpenable(code) {
      if (code === RULE_REVIEW_CODE.WAITING_FOR_PROCESS) {
        return false;
      }

      const definition = this.getRuleActivityItemDefinition(code);
      if (!definition) {
        return false;
      }

      return definition.type !== RULE_REVIEW_CODE_TYPE.COMPLETED;
    },
    headerStyle(code) {
      const definition = this.getRuleActivityItemDefinition(code);
      if (!definition) {
        return { bg: 'bg-muted-foreground', glow: '', icon: null };
      }

      switch (definition.type) {
        case RULE_REVIEW_CODE_TYPE.WAITING:
          return { bg: 'bg-[#FE7002]', glow: 'hover:[--tile-glow:rgb(254_112_2/0.4)]', icon: markRaw(Clock) };

        case RULE_REVIEW_CODE_TYPE.COMPLETED:
          return { bg: 'bg-primary', glow: '', icon: markRaw(CircleCheck) };

        default:
          return {
            bg: 'bg-destructive',
            glow: 'hover:[--tile-glow:color-mix(in_oklab,var(--destructive)_40%,transparent)]',
            icon: markRaw(CircleX),
          };
      }
    },
    getHeaderLabel(code) {
      const definition = this.getRuleActivityItemDefinition(code);

      if (!definition) {
        return '';
      }

      return this.tr(definition.shortLabel[this.entity.action]);
    },
    getContent(item) {
      if (item.entity_type === 'ad') {
        return item.entity.text ?? item.entity.name ?? '';
      }

      return item.entity.text ?? '';
    },
    getDateTooltipPrefix(item) {
      if (item.entity_type === 'ad') {
        return this.tr('Ad created on') + '<br/>';
      }

      return this.tr('Post created on') + '<br/>';
    },
    openFullscreenModal(item) {
      if (!this.isOpenable(item.code)) {
        return;
      }

      this.$fullscreenModal.open(
        this,
        RuleActivityReview,
        {
          item,
          rule: this.entity,
        },
      );
    },
    validate() {
      return true;
    },
  },
};
</script>
